/**
 * The learner's attempts at one quiz / exam — how many, when, and what they scored.
 *
 *   <AttemptsTable attempts={list} selectedId={id} onSelect={fn} passMark={60} maxAttempts={3} />
 *
 * `attempts` comes from GET /exams/{id}/attempts (newest first after useAttempts sorts them).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, XCircle, Clock, Hourglass } from '@phosphor-icons/react';

export const formatDuration = (s) => {
  const t = Math.max(0, Math.round(Number(s) || 0));
  const m = Math.floor(t / 60), sec = t % 60;
  return m ? `${m}m ${String(sec).padStart(2, '0')}s` : `${sec}s`;
};

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** Loads the caller's own attempts at an exam, finished ones first (newest at the top). */
export function useAttempts(api, examId) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(!!examId);
  const reload = useCallback(async () => {
    if (!examId) return;
    setLoading(true);
    try {
      const r = await api.get(`/exams/${examId}/attempts`);
      const done = (r.data || []).filter((a) => ['submitted', 'graded', 'pending_review'].includes(a.status));
      done.sort((a, b) => (b.attempt_number || 0) - (a.attempt_number || 0));
      setAttempts(done);
    } catch { setAttempts([]); }
    finally { setLoading(false); }
  }, [api, examId]);
  useEffect(() => { reload(); }, [reload]);

  const stats = useMemo(() => {
    const scores = attempts.map((a) => a.percentage || 0);
    return {
      count: attempts.length,
      best: scores.length ? Math.max(...scores) : null,
      latest: attempts[0]?.percentage ?? null,
      passed: attempts.some((a) => a.passed),
    };
  }, [attempts]);

  return { attempts, loading, reload, stats };
}

export default function AttemptsTable({ attempts, selectedId, onSelect, passMark = 60, maxAttempts, compact = false }) {
  if (!attempts?.length) {
    return <p className="text-[13px] text-muted-foreground" data-testid="attempts-empty">No attempts yet.</p>;
  }
  const best = Math.max(...attempts.map((a) => a.percentage || 0));
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid="attempts-table">
      <table className="w-full text-[13px]">
        <thead className="bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="text-left font-medium px-3 py-2">Attempt</th>
            <th className="text-left font-medium px-3 py-2">When</th>
            <th className="text-right font-medium px-3 py-2">Marks</th>
            <th className="text-right font-medium px-3 py-2">Score</th>
            {!compact && <th className="text-right font-medium px-3 py-2">Time</th>}
            <th className="text-left font-medium px-3 py-2">Result</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {attempts.map((a) => {
            const isBest = (a.percentage || 0) === best;
            const selected = a.id === selectedId;
            return (
              <tr key={a.id} onClick={onSelect ? () => onSelect(a) : undefined}
                className={`${onSelect ? 'cursor-pointer hover:bg-muted/40' : ''} ${selected ? 'bg-shell-primary/5' : ''}`}
                data-testid={`attempt-row-${a.attempt_number}`}>
                <td className="px-3 py-2 font-medium whitespace-nowrap">
                  #{a.attempt_number}
                  {isBest && attempts.length > 1 && <span className="ml-1.5 rounded px-1 h-4 inline-flex items-center text-[10px] font-semibold uppercase bg-shell-primary/10 text-shell-primary">Best</span>}
                  {selected && <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">viewing</span>}
                </td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{when(a.submitted_at || a.started_at)}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{a.score}/{a.max_score}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium whitespace-nowrap">{(a.percentage || 0).toFixed(0)}%</td>
                {!compact && <td className="px-3 py-2 text-right text-muted-foreground tabular-nums whitespace-nowrap"><span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatDuration(a.time_taken_seconds)}</span></td>}
                <td className="px-3 py-2 whitespace-nowrap">
                  {a.status === 'pending_review' ? (
                    <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase bg-amber-100 text-amber-800"><Hourglass className="w-3 h-3" />Awaiting marking</span>
                  ) : a.passed ? (
                    <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3" weight="fill" />Passed</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-[10px] font-semibold uppercase bg-red-100 text-red-800"><XCircle className="w-3 h-3" weight="fill" />Failed</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-3 py-1.5 bg-muted/30 text-[11px] text-muted-foreground flex flex-wrap gap-x-3">
        <span>{attempts.length} attempt{attempts.length === 1 ? '' : 's'}{maxAttempts ? ` of ${maxAttempts} allowed` : ''}</span>
        <span>· best {best.toFixed(0)}%</span>
        <span>· pass mark {Math.round(passMark)}%</span>
      </div>
    </div>
  );
}
